import {
  Component,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as THREE from 'three';
import { debounceTime, Subject } from 'rxjs';
// @ts-ignore
import FOG from 'vanta/dist/vanta.fog.min';
import { AuthService } from '../../../service/auth.service';
import { GlobalLoadingService } from '../../../shared/global-loading.service';

@Component({
  selector: 'app-vanta-fog-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './vanta-fog-login.component.html',
  styleUrls: ['./vanta-fog-login.component.css'],
})
export class VantaFogLoginComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('vantaBg', { static: false }) vantaRef!: ElementRef;

  private vantaEffect: any;
  private lightningTimer: any;
  private randomizationTimer: any;
  private isTransitioning = false;

  // Injected services
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private globalLoading = inject(GlobalLoadingService);

  // Login form model
  loginModel = { username: '', password: '' };
  loginError: string | null = null;
  loginLoading = false;
  usernameValid = true;

  // Register form model
  registerModel = { username: '', email: '', password: '', confirmPassword: '', deviceId: '' };
  registerError: string | null = null;
  registerLoading = false;

  // Password strength for registration
  passwordStrength = 0;
  private passwordInput$ = new Subject<string>();
  usernameValidReg = true;
  emailValid = true;
  showRules = false;

  ngOnInit(): void {
  // Subscribe to password input with debounce for strength calculation
  this.passwordInput$.pipe(debounceTime(250)).subscribe((pw) => {
    this.passwordStrength = this.evaluatePasswordStrength(pw);
  });

  // Try silent auth on login form initialization
  const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';

  if (
    this.authService.currentUserValue &&
    this.authService.currentUserValue.accessToken &&
    !this.authService.isTokenExpired()
  ) {
    this.router.navigateByUrl(returnUrl);
    return;
  }

  // Try silent refresh
  this.loginLoading = true;
  this.globalLoading.show();
  this.authService.refreshToken().subscribe({
    next: () => {
      this.loginLoading = false;
      this.globalLoading.hide();
      this.router.navigateByUrl(returnUrl);
    },
    error: () => {
      this.loginLoading = false;
      this.globalLoading.hide();
    },
  });
}

  ngAfterViewInit(): void {
    // Initialize Vanta background
    this.vantaEffect = FOG({
      el: this.vantaRef.nativeElement,
      THREE: THREE,
      mouseControls: true,
      touchControls: true,
      gyroControls: false,
      minHeight: 200.0,
      minWidth: 200.0,
      highlightColor: 0x0,
      midtoneColor: 0xffffff,
      lowlightColor: 0xd9d9d9,
      baseColor: 0x0,
      blurFactor: 0.62,
      speed: 1.4,
      zoom: 1.6,
    });

    // Start randomizing lightning positions on interval
    this.startRandomLightningPositions();

    // Start lightning effect
    this.startLightningEffect();

    // Initialize form switching
    this.initFormSwitching();

    // Setup form validation
    this.setupFormValidation();
  }

  private startRandomLightningPositions(): void {
    // Randomize immediately on start
    this.randomizeLightningPositions();

    // Get animation durations from your CSS
    const animationDurations = [2.2, 2.5, 2.8]; // Match your CSS animation times
    const maxDuration = Math.max(...animationDurations) * 1000; // ms

    // Randomize again every time animations complete
    this.randomizationTimer = setInterval(() => {
      this.randomizeLightningPositions();
    }, maxDuration);
  }

  private randomizeLightningPositions(): void {
    const svgContainer =
      this.vantaRef.nativeElement.querySelector('.visual-container');

    // Get all lightning bolts
    const bolt1 = svgContainer?.querySelector('.thunder-bolt-1');
    const bolt2 = svgContainer?.querySelector('.thunder-bolt-2');
    const bolt3 = svgContainer?.querySelector('.thunder-bolt-3');
    const boltBehind1 = svgContainer?.querySelector('.thunder-bolt-behind-1');
    const boltBehind2 = svgContainer?.querySelector('.thunder-bolt-behind-2');

    const bolts = [bolt1, bolt2, bolt3, boltBehind1, boltBehind2];
    const maxHorizontalShift = 350;
    const maxVerticalShift = 350;

    bolts.forEach((bolt) => {
      if (bolt) {
        const randomX =
          Math.random() * maxHorizontalShift - maxHorizontalShift / 2;
        const randomY = Math.random() * maxVerticalShift - maxVerticalShift / 2;
        const randomRotate = Math.random() * 15;

        bolt.style.transform = `translateX(${randomX}px) translateY(${randomY}px) rotate(${randomRotate}deg)`;
      }
    });
  }

  private startLightningEffect(): void {
    const overlay =
      this.vantaRef.nativeElement.querySelector('.lightning-overlay');

    const triggerLightning = () => {
      overlay.style.transition = `opacity ${
        Math.random() * 0.4 + 0.2
      }s ease-out`;
      overlay.classList.add('flash');
      setTimeout(() => overlay.classList.remove('flash'), 300);

      if (Math.random() > 0.5) {
        setTimeout(() => overlay.classList.add('flash'), 150);
        setTimeout(() => overlay.classList.remove('flash'), 400);
      }
    };

    const randomInterval = () => Math.random() * 7000 + 3000;

    const loop = () => {
      triggerLightning();
      this.lightningTimer = setTimeout(loop, randomInterval());
    };

    loop();
  }

  private initFormSwitching(): void {
    const loginForm = document.getElementById('loginForm');
    const registerForm = document.getElementById('registerForm');
    const switchToRegister = document.getElementById('switchToRegister');
    const switchToLogin = document.getElementById('switchToLogin');

    switchToRegister?.addEventListener('click', () => {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      loginForm?.classList.add('slide-out-left');
      loginForm?.classList.remove('active');
      setTimeout(() => {
        registerForm?.classList.remove('slide-in-left');
        registerForm?.classList.add('active');
        setTimeout(() => {
          this.isTransitioning = false;
        }, 800);
      }, 100);
    });

    switchToLogin?.addEventListener('click', () => {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      registerForm?.classList.add('slide-out-left');
      registerForm?.classList.remove('active');
      setTimeout(() => {
        loginForm?.classList.remove('slide-out-left');
        loginForm?.classList.add('active');
        setTimeout(() => {
          this.isTransitioning = false;
        }, 800);
      }, 100);
    });
  }

  private setupFormValidation(): void {
    const loginFormElement = document.getElementById(
      'loginFormElement',
    ) as HTMLFormElement;
    const registerFormElement = document.getElementById(
      'registerFormElement',
    ) as HTMLFormElement;

    // --- Password strength UI and rules display ---
    const passwordField = document.getElementById(
      'registerPassword',
    ) as HTMLInputElement;
    const confirmPasswordField = document.getElementById(
      'registerConfirmPassword',
    ) as HTMLInputElement;
    const strengthFill = document.getElementById(
      'passwordStrengthFill',
    ) as HTMLElement;
    const passwordStrengthContainer = document.getElementById(
      'passwordStrengthContainer',
    );

    const togglePasswordBtn = document.getElementById('togglePassword');
    const toggleConfirmPasswordBtn = document.getElementById('toggleConfirmPassword');

    const rulesList = {
      length: document.getElementById('pwLength'),
      upper: document.getElementById('pwUpper'),
      number: document.getElementById('pwNumber'),
      symbol: document.getElementById('pwSymbol'),
    };

    // Hide password meter and rules initially
    passwordStrengthContainer?.classList.add('hide');

    // Show password meter on password focus
    passwordField?.addEventListener('focus', () => {
      passwordStrengthContainer?.classList.remove('hide');
    });

    passwordField?.addEventListener('blur', () => {
      if (passwordField.value === '') {
        passwordStrengthContainer?.classList.add('hide');
      }
    });

    // Toggle password visibility
    togglePasswordBtn?.addEventListener('click', () => {
      const img = togglePasswordBtn.querySelector('img') as HTMLImageElement;
      if (passwordField.type === 'password') {
        passwordField.type = 'text';
        if (img) img.src = 'login/hide-password-eye-royalty.png';
      } else {
        passwordField.type = 'password';
        if (img) img.src = 'login/password-eye-royalty.png';
      }
    });

    toggleConfirmPasswordBtn?.addEventListener('click', () => {
      const img = toggleConfirmPasswordBtn.querySelector(
        'img',
      ) as HTMLImageElement;
      if (confirmPasswordField.type === 'password') {
        confirmPasswordField.type = 'text';
        if (img) img.src = 'login/hide-password-eye-royalty.png';
      } else {
        confirmPasswordField.type = 'password';
        if (img) img.src = 'login/password-eye-royalty.png';
      }
    });

    // Password input event for live strength and rule updates
    passwordField?.addEventListener('input', () => {
      const val = passwordField.value;
      const hasLength = val.length >= 8;
      const hasUpper = /[A-Z]/.test(val);
      const hasNumber = /\d/.test(val);
      const hasSymbol = /[\W_]/.test(val);

      let passedCount = [hasLength, hasUpper, hasNumber, hasSymbol].filter(
        Boolean,
      ).length;
      let percent = passedCount * 25;

      strengthFill.style.width = percent + '%';

      // Manage glow class by strength
      ['level-2', 'level-3', 'level-4'].forEach((cls) =>
        strengthFill.classList.remove(cls),
      );

      if (passedCount > 1) strengthFill.classList.add('level-2');
      if (passedCount > 2) strengthFill.classList.add('level-3');
      if (passedCount > 3) strengthFill.classList.add('level-4');

      // Update rule classes
      if (hasLength) rulesList.length?.classList.add('passed');
      else rulesList.length?.classList.remove('passed');

      if (hasUpper) rulesList.upper?.classList.add('passed');
      else rulesList.upper?.classList.remove('passed');

      if (hasNumber) rulesList.number?.classList.add('passed');
      else rulesList.number?.classList.remove('passed');

      if (hasSymbol) rulesList.symbol?.classList.add('passed');
      else rulesList.symbol?.classList.remove('passed');
    });

    // --- Login form submission ---
    loginFormElement?.addEventListener('submit', (e) => {
      e.preventDefault();

      const email = (
        document.getElementById('loginEmail') as HTMLInputElement
      ).value;
      const password = (
        document.getElementById('loginPassword') as HTMLInputElement
      ).value;

      let isValid = true;

      if (!this.validateEmail(email)) {
        this.showError('loginEmail', 'loginEmailError', true);
        isValid = false;
      } else {
        this.showError('loginEmail', 'loginEmailError', false);
      }

      if (password.length === 0) {
        this.showError('loginPassword', 'loginPasswordError', true);
        isValid = false;
      } else {
        this.showError('loginPassword', 'loginPasswordError', false);
      }

      if (isValid) {
        this.onLoginSubmit();
      }
    });

    // --- Register form submission ---
    registerFormElement?.addEventListener('submit', (e) => {
      e.preventDefault();

      const name = (
        document.getElementById('registerName') as HTMLInputElement
      ).value;
      const email = (
        document.getElementById('registerEmail') as HTMLInputElement
      ).value;
      const password = (
        document.getElementById('registerPassword') as HTMLInputElement
      ).value;
      const confirmPassword = (
        document.getElementById('registerConfirmPassword') as HTMLInputElement
      ).value;
      const deviceId = (
        document.getElementById('registerDeviceId') as HTMLInputElement
      ).value;

      let isValid = true;

      if (name.length === 0) {
        this.showError('registerName', 'registerNameError', true);
        isValid = false;
      } else {
        this.showError('registerName', 'registerNameError', false);
      }

      if (!this.validateEmail(email)) {
        this.showError('registerEmail', 'registerEmailError', true);
        isValid = false;
      } else {
        this.showError('registerEmail', 'registerEmailError', false);
      }

      const hasLength = password.length >= 8;
      const hasUpper = /[A-Z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSymbol = /[\W_]/.test(password);

      if (!(hasLength && hasUpper && hasNumber && hasSymbol)) {
        this.showError('registerPassword', 'registerPasswordError', true);
        isValid = false;
      } else {
        this.showError('registerPassword', 'registerPasswordError', false);
      }

      if (password !== confirmPassword) {
        this.showError(
          'registerConfirmPassword',
          'registerConfirmPasswordError',
          true,
        );
        isValid = false;
      } else {
        this.showError(
          'registerConfirmPassword',
          'registerConfirmPasswordError',
          false,
        );
      }

      if (deviceId.length === 0) {
        this.showError('registerDeviceId', 'registerDeviceIdError', true);
        isValid = false;
      } else {
        this.showError('registerDeviceId', 'registerDeviceIdError', false);
      }

      if (isValid) {
        this.onRegisterSubmit(name, email, password, deviceId);
      }
    });
  }

  // Login submission handler
  private onLoginSubmit(): void {
    this.loginError = null;
    this.loginLoading = true;
    this.globalLoading.show();

    const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';

    this.authService.login(this.loginModel.username, this.loginModel.password).subscribe({
      next: () => {
        this.loginLoading = false;
        this.globalLoading.hide();
        this.showSuccess('loginForm', '⚡ Welcome back! Login successful.');
        setTimeout(() => {
          this.router.navigateByUrl(returnUrl);
        }, 1500);
      },
      error: (err) => {
        this.loginLoading = false;
        this.globalLoading.hide();
        this.loginError = err?.error?.message ||
          this.showSuccess('loginSuccess', 'Prijava neuspješna, provjerite unesene podatke.');
        
      },
    });
  }

  // Register submission handler
  private onRegisterSubmit(
    username: string,
    email: string,
    password: string,
    deviceId: string,
  ): void {
    this.registerError = null;
    this.registerLoading = true;
    this.globalLoading.show();

    this.authService
      .register({ username, email, password, deviceId })
      .subscribe({
        next: () => {
          this.registerLoading = false;
          this.globalLoading.hide();
          this.showSuccess(
            'registerSuccess',
            '🚴 Account created! Welcome aboard.',
          );
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 1500);
        },
        error: (err) => {
          this.registerLoading = false;
          this.globalLoading.hide();
          this.registerError =
            err?.error?.message || 'Registracija nije uspjela.';
          this.showError('registerForm', 'registerFormError', true);
        },
      });
  }

  // Validation helper methods
  private validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private evaluatePasswordStrength(pw: string): number {
    let score = 0;
    if (!pw) return 0;
    if (pw.length >= 8) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  }

  private showError(
    inputId: string,
    errorId: string,
    show: boolean,
  ): void {
    const input = document.getElementById(inputId);
    const error = document.getElementById(errorId);

    if (show) {
      input?.classList.add('error');
      error?.classList.add('show');
    } else {
      input?.classList.remove('error');
      error?.classList.remove('show');
    }
  }

  private showSuccess(elementId: string, message: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.textContent = message;
      element.classList.add('show');
      setTimeout(() => {
        element.classList.remove('show');
      }, 5000);
    }
  }

  // Public methods for template
  onLoginUsernameInput(value: string): void {
    this.usernameValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  onRegisterUsernameInput(value: string): void {
    this.usernameValidReg = !!value && value.length >= 3;
  }

  onRegisterEmailInput(value: string): void {
    this.emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  onPasswordInput(): void {
    this.passwordInput$.next(this.registerModel.password || '');
  }

  toggleRules(open?: boolean): void {
    this.showRules =
      typeof open === 'boolean' ? open : !this.showRules;
  }

  get passwordsMatch(): boolean {
    return this.registerModel.password === this.registerModel.confirmPassword;
  }

  ngOnDestroy(): void {
    if (this.vantaEffect) this.vantaEffect.destroy();
    if (this.lightningTimer) clearTimeout(this.lightningTimer);
    if (this.randomizationTimer) clearInterval(this.randomizationTimer);
  }
}
